import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Button, Divider, Form, Input, Select, Spin, Tag, message } from "antd";
import { apiCallback } from "../../../ApiHandlers/Common";
import {
  EntityStatus,
  ConfigActions,
  ConfigEntity,
  tailItemLayout,
  formItemLayout,
  ConfigJSON,
} from "../../../Constants/Constants";
import {
  jsonValidator,
  selectFilterFunction,
} from "../../../Constants/Utilities";
import { getConfigFormTitle } from "../../../Helpers/ConfigHelpers";
import { onFormSubmit } from "../../../ApiHandlers/ConfigApiHandlers";
import { convertServiceSimpleList } from "../../../Helpers/ServiceHelpers";
import { DiscardWarning } from "../../Common/DiscardWarning";
import { BreadCrumbs } from "../../Common/BreadCrumbs";

const getInitialValues = (record) => {
  const initialValues = {
    status: EntityStatus.ACTIVE,
    config: ConfigJSON,
  };
  if (record) {
    initialValues.name = record.name;
    initialValues.status = record.status;
    initialValues.modelId = record.deviceModelId;
    initialValues.serviceId = record.serviceId;
    initialValues.config = JSON.stringify(record.config, null, 4);
  }
  return initialValues;
};

const DeviceModelConfigForm = (props) => {
  console.log("DeviceForm props", props);
  const { action, history, record, onActionClick, serviceApi } = props;

  const title = useMemo(() => getConfigFormTitle(action), []);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  // service info
  const [serviceDataLoading, setserviceDataLoading] = useState(false);
  const [serviceData, setserviceData] = useState([]);

  const [form] = Form.useForm();

  const [processing, setProcessing] = useState(false);
  const isAddForm = props.action === ConfigActions.add;
  const isViewForm = props.action === ConfigActions.view;

  useEffect(() => {
    updateServiceList();
  }, []);

  const updateServiceList = () => {
    console.log("fetchserviceList...");
    setserviceDataLoading(true);
    const errorCallback = (error) => {
      setserviceDataLoading(false);
      message.error("Error fetching service list");
    };
    const successCallback = (data) => {
      const serviceData = convertServiceSimpleList(data);
      setserviceDataLoading(false);
      console.log("updateData data", serviceData);
      setserviceData(serviceData);
    };
    try {
      serviceApi.getService(
        apiCallback({ history, errorCallback, successCallback })
      );
    } catch (error) {
      errorCallback(error);
    }
  };

  const onBackClick = () => {
    const isChanged =
      action === ConfigActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = ["name", "modelId", "serviceId", "config", "status"];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(
            `${title} Device Model Configuration`
          )}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            layout="horizontal"
            name="device-model-config-form"
            onFinish={onFormSubmit({
              setInitialValues,
              props,
              setProcessing,
              entity: ConfigEntity.deviceModel,
            })}
            initialValues={initialValues}
            requiredMark={"optional"}
            size={"middle"}
            {...formItemLayout}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Form.Item
                name="name"
                label="Configuration Name"
                rules={[
                  { required: true, message: "Please fill in this field" },
                ]}
              >
                <Input
                  title="Name should be unique"
                  style={{ width: "90%", maxWidth: "300px" }}
                  disabled={!isAddForm}
                />
              </Form.Item>

              <Form.Item
                name="modelId"
                label="Device Model:"
                rules={[
                  { required: true, message: "Please select Device Model" },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={props.modelDataLoading}
                  disabled={props.modelDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    props.modelDataLoading
                      ? "Loading Device Model list..."
                      : "Select Device Model"
                  }
                >
                  {props.modelData.map((item) => (
                    <Select.Option key={item.modelId} title={item.modelNumber}>
                      {item.modelNumber}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="serviceId"
                label="Service:"
                rules={[{ required: true, message: "Please select Service" }]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={props.serviceDataLoading}
                  disabled={props.serviceDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    props.serviceDataLoading
                      ? "Loading Service list..."
                      : "Select Service"
                  }
                >
                  {serviceData.map((item) => (
                    <Select.Option
                      key={item.serviceId}
                      title={item.serviceName}
                    >
                      {item.serviceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="config"
                label="Configuration"
                validateTrigger={["onBlur"]}
                rules={[{ required: true, validator: jsonValidator() }]}
              >
                <Input.TextArea
                  title="Configuration must be JSON object"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  style={{ width: "90%", maxWidth: "500px" }}
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Tag
                  color={
                    initialValues.status === EntityStatus.ACTIVE
                      ? "green"
                      : "red"
                  }
                >
                  {initialValues.status}
                </Tag>
              </Form.Item>

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};

DeviceModelConfigForm.propTypes = {
  action: PropTypes.string,
  record: PropTypes.object,
  modelData: PropTypes.array,
  modelDataLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  onActionClick: PropTypes.func,
  parentBread: PropTypes.array,

  serviceApi: PropTypes.object,
  configManagementApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  serviceApi: state.apis.serviceApi,
  configManagementApi: state.apis.configManagementApi,
});
const connectedDeviceModelConfigForm = connect(mapStateToProps)(
  DeviceModelConfigForm
);
export default withRouter(connectedDeviceModelConfigForm);
